import { LocalizedLink } from "@/components/LocalizedLink";
import { ArrowRight } from "lucide-react";

type BathFamily = "bathtub" | "basin" | "vanity";

interface BathItem {
  slug: string;
  name: string;
  tagline: string;
  image: string;
  family: BathFamily;
}

const CATALOGUE: BathItem[] = [
  { slug: "aria-sculpt", name: "ARIA Sculpt Bathtub", tagline: "Freestanding fluted solid surface", image: "/images/bath/aria-sculpt-hero.jpg", family: "bathtub" },
  { slug: "aria-monolith", name: "ARIA Monolith Tub", tagline: "Architectural oval solid surface", image: "/images/bath/aria-monolith.jpg", family: "bathtub" },
  { slug: "aria-diamond", name: "ARIA Diamond Bathtub", tagline: "Diamond-texture freestanding solid surface", image: "/images/bath/aria-diamond-hero.jpg", family: "bathtub" },
  { slug: "vena-basin", name: "VENA Counter Basin", tagline: "Sculpted solid surface washbasin", image: "/images/bath/vena-basin.jpg", family: "basin" },
  { slug: "vena-vanity", name: "VENA Vanity Console", tagline: "Integrated bathroom vanity solution", image: "/images/bath/vena-vanity.jpg", family: "vanity" },
];

function pickRelated(currentSlug: string, max = 4): BathItem[] {
  const current = CATALOGUE.find((c) => c.slug === currentSlug);
  const others = CATALOGUE.filter((c) => c.slug !== currentSlug);
  if (!current) return others.slice(0, max);

  const sameFamily = others.filter((o) => o.family === current.family);
  const complement: BathItem[] = others.filter((o) => {
    if (current.family === "basin") return o.family === "vanity";
    if (current.family === "vanity") return o.family === "basin";
    return false;
  });
  const rest = others.filter((o) => !sameFamily.includes(o) && !complement.includes(o));
  return [...sameFamily, ...complement, ...rest].slice(0, max);
}

export const RelatedBathProducts = ({ currentSlug }: { currentSlug: string }) => {
  const items = pickRelated(currentSlug, 4);
  if (items.length === 0) return null;

  return (
    <section className="border-b border-neutral-200 bg-neutral-50 py-20">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">
              Related Products
            </p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
              Continue exploring the <span className="text-amber-600">bath collection</span>
            </h2>
            <div className="mt-3 h-px w-16 bg-amber-500" />
          </div>
          <LocalizedLink
            to="/bath-sanitary"
            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-900 hover:text-amber-600"
          >
            View all bath products <ArrowRight className="h-4 w-4" />
          </LocalizedLink>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((p) => (
            <LocalizedLink
              key={p.slug}
              to={`/bath-sanitary/${p.slug}`}
              className="group block overflow-hidden border border-neutral-200 bg-white transition-all hover:border-amber-500 hover:shadow-lg"
              aria-label={`View ${p.name} product page`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-neutral-100">
                <img
                  src={p.image}
                  alt={`${p.name} luxury bathroom fixture by Aureum VII Trading — ${p.tagline}`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.opacity = "0.2";
                  }}
                />
              </div>
              <div className="p-4">
                <h3 className="font-serif text-base font-semibold text-neutral-900 group-hover:text-amber-600">
                  {p.name}
                </h3>
                <p className="mt-1 text-xs text-neutral-500">{p.tagline}</p>
                <p className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                  View product <ArrowRight className="h-3 w-3" />
                </p>
              </div>
            </LocalizedLink>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedBathProducts;
